import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LoginService, ToastService } from '@app/services';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

// For now.
export interface GenericResponse {
  errorFound: boolean;
  publicMessage: string;
  /**
   * * 1 Equals Success
   * * 2 Equals Success and Display Response
   * * 3 Equals Error
   * * 4 Equals API error and display Response to the User.
   * * 5 Equals Redirect to Login Page
   * * 7 Equals Redirect to Home Page
   * * 8 Equals Go Back One Page
   * * 9 Equals Refresh User Claims
   * * 10 Equals Success but there were some failures.
   */
  responseCode: number;
  responseMessage: string;
  responseData?: any;
  statusCode: number;
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  retryTimes = 0;

  constructor(
    // private app: AppService,
    private router: Router,
    private loginService: LoginService,
    private auth: AuthService,
    private dialog: MatDialog,
    private toast: ToastService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler, retriedTimes: number = 0): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(val => {
        if (val instanceof HttpResponse) {
          if (val.body?.errorFound) {
            throw val.body;
          }
        }
        this.retryTimes = retriedTimes;
      }),
      switchMap(r => {
        let returnValue: Observable<HttpEvent<unknown>> = of(r);
        let obsToReturn: Observable<any> | undefined;

        // First: Check if r is an instance of GenericResponse. Else just return the response.
        if (r instanceof HttpResponse && r.body && this.isGenericResponse(r.body)) {
          // This is probably a GenericResponse.
          const resp: GenericResponse = r.body;
          obsToReturn = this.handleResponseCode(resp);

          if (resp.responseData !== undefined) {
            const copy = { ...r };
            copy.body = resp.responseData;
            returnValue = of(new HttpResponse(copy));
          }
        }
        return obsToReturn
          ? obsToReturn.pipe(
              switchMap(() => {
                return returnValue;
              })
            )
          : returnValue;
      }),
      catchError((err: HttpErrorResponse): Observable<any> => {
        if (err instanceof HttpErrorResponse && this.isGenericResponse(err.error)) {
          const resp: GenericResponse = err.error;
          const obsToReturn: Observable<any> = this.handleResponseCode(resp);
          if (obsToReturn) {
            if (resp.responseCode == 9) {
              // retry this request with updated jwt.
              return obsToReturn.pipe(
                take(1), // This legit should not make a difference.
                switchMap((accessToken: string) => {
                  const authenticatedRequest = request.clone({
                    headers: request.headers.set('Authorization', 'Bearer ' + accessToken),
                  });
                  // return next.handle(authenticatedRequest);
                  return this.intercept(authenticatedRequest, next);
                })
              );
            }
            return obsToReturn;
          }

          const copy = { ...err };
          if (resp.responseData !== undefined) {
            copy.error = resp.responseData;
          } else {
            copy.error = resp.publicMessage;
          }
          // this.app.loading = false;
          return throwError(() => new HttpErrorResponse(copy));
        } else {
          // Unauthorized. No idea when this will happen.
          if (err.status == 401) {
            if (this.loginService.user != undefined) {
              if (this.retryTimes < 2) {
                return this.auth.getAccessTokenSilently().pipe(
                  switchMap(accessToken => {
                    const authenticatedRequest = request.clone({
                      headers: request.headers.set('Authorization', 'Bearer ' + accessToken),
                    });
                    console.log('retrieved another access token and retrying the request');
                    return this.intercept(authenticatedRequest, next, this.retryTimes + 1);
                  })
                );
              } else {
                console.warn('Stopped retrying this request due to repeated failures. ');
              }
            } else {
              this.loginService.logOut('/');
              this.router.navigate(['/']);
            }
            this.retryTimes = 0;
          }
        }

        // this.app.loading = false;
        return throwError(() => new Error(err.message));
      })
    );
  }

  private isGenericResponse(val: any): val is GenericResponse {
    return (
      val != null && typeof val === 'object' && 'errorFound' in val && 'publicMessage' in val && 'responseCode' in val
      // 'responseData' in val
    );
  }

  private handleResponseCode(resp: GenericResponse): Observable<any> | undefined {
    switch (resp.responseCode) {
      case 2:
        this.toast.success(resp.publicMessage);
        break;
      case 3:
        break;
      case 4:
        this.toast.error(resp.publicMessage);
        break;
      case 5:
        if (resp.errorFound) {
          this.toast.error(resp.publicMessage);
        } else {
          this.toast.info(resp.publicMessage);
        }

        // If this user is unauthorized or unauthenticated, then log out and log in again, (hopefully as the correct user.)
        if (resp.statusCode == 403 || resp.statusCode == 401) {
          setTimeout(() => {
            this.loginService.logOut('/login');
            // this.loginService.logOut("/login?target="+window.location.pathname); // We don't  want to do this, this will stick us in an infinite loop.
          }, 3000);
        } else {
          setTimeout(() => {
            this.router.navigate(['/']);
          }, 3000);
        }
        break;
      case 6:
        if (resp.errorFound) {
          this.toast.error(resp.publicMessage);
        } else {
          this.toast.info(resp.publicMessage);
        }
        setTimeout(() => {
          this.loginService.logOut();
        }, 3000);
        break;
      case 7:
        if (resp.errorFound) {
          this.toast.error(resp.publicMessage);
        } else {
          this.toast.info(resp.publicMessage);
        }
        // Must navigate to user's homepage.
        this.loginService.navigateHome();
        break;
      case 8:
        if (resp.errorFound) {
          this.toast.error(resp.publicMessage);
        } else {
          this.toast.info(resp.publicMessage);
        }
        setTimeout(() => {
          window.history.back();
        }, 3000);
        break;
      case 9:
        return this.auth.getAccessTokenSilently({
          cacheMode: 'off',
        });
    }
    return undefined;
  }
}
